// A minimal ZIP writer, enough to hand a browser a single file containing
// a whole batch of results.
//
// Why in the browser: Store exposes exactly one artifact at a time
// (05-store.md: `GET /artifacts/{id}`, no listing, no multi-read) and Core
// has no bundle endpoint, so a server-side zip would be a new contract --
// and 00-overview.md is explicit that zip/bundle semantics are not part of
// V1 ("Cartesian product always. No zip."). Zipping bytes the browser has
// already fetched needs no backend change at all and is the only way to
// offer this today. If a server-side bundle is ever wanted, it should
// arrive as its own contract, not be smuggled in through the UI.
//
// Method 0 (stored), no zip64: the payloads are already-compressed
// mp4/jpg artifacts, so deflating them buys nothing, and a batch too large
// for the format is refused rather than silently truncated by it.
import { CRC32 } from "./crc32";

export interface ZipEntry {
  /** Path inside the archive, `/`-separated. */
  name: string;
  /** Pinned to ArrayBuffer rather than ArrayBufferLike: a Blob cannot be
   * built from a SharedArrayBuffer-backed view, and saying so here keeps
   * the constraint at the type instead of a cast at the call site. */
  data: Uint8Array<ArrayBuffer>;
}

/** 4 GiB is the whole addressable range of a non-zip64 archive's offsets,
 * and one byte more than a uint32 can express. */
export const ZIP_MAX_TOTAL_BYTES = 0xffffffff;

const LOCAL_SIG = 0x04034b50;
const CENTRAL_SIG = 0x02014b50;
const EOCD_SIG = 0x06054b50;
/** bit 11: names are UTF-8 (APPNOTE 4.4.4). Set on every entry. */
const FLAG_UTF8 = 0x0800;
const LOCAL_HEADER = 30;
const CENTRAL_HEADER = 46;
const EOCD = 22;

/** MS-DOS date/time, the only timestamp format ZIP has. */
function dosStamp(d: Date): { time: number; date: number } {
  const year = Math.max(1980, d.getFullYear());
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1),
    date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

/** The archive as bytes. Split out from buildZip so the format can be
 * tested as what it is -- a byte layout -- without a Blob in the way. */
export function buildZipBytes(entries: ZipEntry[], now: Date = new Date()): Uint8Array<ArrayBuffer> {
  if (entries.length === 0) throw new Error("zip: no entries");
  if (entries.length > 0xffff) throw new Error("zip: too many entries for a non-zip64 archive");

  const names = entries.map((e) => new TextEncoder().encode(e.name));
  let total = EOCD;
  for (let i = 0; i < entries.length; i++) {
    total += LOCAL_HEADER + names[i].length + entries[i].data.length + CENTRAL_HEADER + names[i].length;
  }

  const { time, date } = dosStamp(now);
  const out = new Uint8Array(total);
  const view = new DataView(out.buffer);
  const offsets: number[] = [];
  let p = 0;

  // Local headers and payloads, in order.
  for (let i = 0; i < entries.length; i++) {
    const { data } = entries[i];
    const name = names[i];
    const crc = CRC32.of(data);
    offsets.push(p);

    view.setUint32(p, LOCAL_SIG, true);
    view.setUint16(p + 4, 20, true); // version needed: 2.0
    view.setUint16(p + 6, FLAG_UTF8, true);
    view.setUint16(p + 8, 0, true); // method: stored
    view.setUint16(p + 10, time, true);
    view.setUint16(p + 12, date, true);
    view.setUint32(p + 14, crc, true);
    view.setUint32(p + 18, data.length, true);
    view.setUint32(p + 22, data.length, true);
    view.setUint16(p + 26, name.length, true);
    view.setUint16(p + 28, 0, true); // extra field length
    out.set(name, p + LOCAL_HEADER);
    p += LOCAL_HEADER + name.length;
    out.set(data, p);
    p += data.length;
  }

  // Central directory.
  const centralStart = p;
  for (let i = 0; i < entries.length; i++) {
    const { data } = entries[i];
    const name = names[i];
    const crc = CRC32.of(data);

    view.setUint32(p, CENTRAL_SIG, true);
    view.setUint16(p + 4, 20, true); // version made by
    view.setUint16(p + 6, 20, true); // version needed
    view.setUint16(p + 8, FLAG_UTF8, true);
    view.setUint16(p + 10, 0, true); // method: stored
    view.setUint16(p + 12, time, true);
    view.setUint16(p + 14, date, true);
    view.setUint32(p + 16, crc, true);
    view.setUint32(p + 20, data.length, true);
    view.setUint32(p + 24, data.length, true);
    view.setUint16(p + 28, name.length, true);
    view.setUint16(p + 30, 0, true); // extra
    view.setUint16(p + 32, 0, true); // comment
    view.setUint16(p + 34, 0, true); // disk number start
    view.setUint16(p + 36, 0, true); // internal attributes
    view.setUint32(p + 38, 0, true); // external attributes
    view.setUint32(p + 42, offsets[i], true);
    out.set(name, p + CENTRAL_HEADER);
    p += CENTRAL_HEADER + name.length;
  }

  // End of central directory.
  view.setUint32(p, EOCD_SIG, true);
  view.setUint16(p + 4, 0, true); // this disk
  view.setUint16(p + 6, 0, true); // disk with the central directory
  view.setUint16(p + 8, entries.length, true);
  view.setUint16(p + 10, entries.length, true);
  view.setUint32(p + 12, p - centralStart, true);
  view.setUint32(p + 16, centralStart, true);
  view.setUint16(p + 20, 0, true); // comment length

  return out;
}

export function buildZip(entries: ZipEntry[], now: Date = new Date()): Blob {
  return new Blob([buildZipBytes(entries, now)], { type: "application/zip" });
}

/** Make entry names unique without losing their meaning: `a.png`,
 * `a-2.png`, `a-3.png`. Two slots of one task can legitimately map to the
 * same readable name (a task with no coordinates at all), and a zip with
 * duplicate names unzips to fewer files than it claims. */
export function uniqueNames(names: string[]): string[] {
  const seen = new Map<string, number>();
  return names.map((name) => {
    const n = (seen.get(name) ?? 0) + 1;
    seen.set(name, n);
    if (n === 1) return name;
    const dot = name.lastIndexOf(".");
    const slash = name.lastIndexOf("/");
    if (dot > slash + 1) return `${name.slice(0, dot)}-${n}${name.slice(dot)}`;
    return `${name}-${n}`;
  });
}

/** Control bytes, by code point rather than a character class full of
 * escapes: same result, and the intent stays legible. */
function isControl(ch: string): boolean {
  const code = ch.charCodeAt(0);
  return code < 0x20 || code === 0x7f;
}

/** Strip anything that would let a filename escape the archive or the
 * filesystem it is extracted into. Slot names are built from dimension
 * names that come from the client's own inputs today, but a download name
 * is not a place to trust input. */
export function sanitizeName(name: string): string {
  const kept = name
    .replace(/\\/g, "/")
    .split("/")
    .filter((part) => part !== "" && part !== "." && part !== "..")
    .join("/")
    .split("")
    .map((ch) => (isControl(ch) ? "_" : ch))
    .join("");
  return kept.replace(/[<>:"|?*]/g, "_").slice(0, 200);
}
